import React, { FC, useMemo } from 'react';
import Lozenge from '@atlaskit/lozenge';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import Popup from '@atlaskit/popup';
import isNil from 'lodash/isNil';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { LowTrafficStatusDTO } from '@atlassiansox/phobos-client-ts';

import { ExternalLinkAction } from '@/components/primitive';
import { DAC_FAQS_DOC_LINK } from '@/constants';
import { useOpenableController } from '@/controllers';
import { flexCenter, popupContentStyles, spaceGapCompact, spaceTopComfortable } from '@/styles';
import { localNumberFormat } from '@/utils';

import LowTrafficPopup from './low-traffic-popup';
import { getLowTrafficThreshold } from './utils';
import { LOW_TRAFFIC_DEFAULT_ERROR_TOLERANCE, thresholdTooLowMessage } from './constants';

const LowTrafficHelper: FC<{
  objective: number;
  publicId: string;
  lowTrafficStatus: LowTrafficStatusDTO;
}> = ({ objective, publicId, lowTrafficStatus }) => {
  const openableController = useOpenableController();

  const thresholdRecommended = useMemo(
    () => getLowTrafficThreshold(objective, LOW_TRAFFIC_DEFAULT_ERROR_TOLERANCE),
    [objective]
  );

  // Still fetching, don't show anything
  if (!lowTrafficStatus) return <></>;

  // "inLowTrafficState" in backend is currently determined by checking if the
  // traffic in window is less than configured threshold (or default value if
  // not configure).
  const { inLowTrafficState, currentTraffic, lowTrafficThreshold } = lowTrafficStatus;

  // No data, don't show anything
  if (!currentTraffic) return <></>;

  // Note: it could also be 0 as a configured value
  const thresholdConfigured = !isNil(lowTrafficThreshold);

  // Threshold not configured but not in low traffic state, don't show anything.
  // This means the traffic is larger than the default threshold
  if (!inLowTrafficState && !thresholdConfigured) return <></>;

  // Threshold has been configured but less than the recommended value, show warning.
  const thresholdConfiguredTooLow =
    thresholdConfigured && lowTrafficThreshold < thresholdRecommended;

  return (
    <div css={spaceTopComfortable}>
      <SectionMessage
        appearance={thresholdConfigured ? 'discovery' : 'warning'}
        actions={[
          <SectionMessageAction key="low-traffic-message" href="#">
            <Popup
              placement="auto"
              isOpen={openableController.isOpen}
              onClose={openableController.onClose}
              trigger={props => (
                <div
                  {...props}
                  onClick={e => {
                    e.preventDefault();
                    openableController.onToggle();
                  }}
                >
                  See details
                </div>
              )}
              content={() => (
                <div css={popupContentStyles}>
                  <LowTrafficPopup
                    lowTrafficStatus={lowTrafficStatus}
                    objective={objective}
                    publicId={publicId}
                    closePopup={openableController.onClose}
                  />
                </div>
              )}
            />
          </SectionMessageAction>,
          <SectionMessageAction key="low-traffic-warning-action-find-out-more" href="#">
            <ExternalLinkAction
              text="Find out more"
              href={`${DAC_FAQS_DOC_LINK}low-traffic-slos/#what-is-low-traffic-threshold-`}
            />
          </SectionMessageAction>,
        ]}
      >
        <p css={[flexCenter, spaceGapCompact]}>
          {inLowTrafficState && <span>Low traffic is detected for this SLO. </span>}
          {!thresholdConfigured && (
            <>
              <span>Please configure a </span>
              <Lozenge>low traffic threshold</Lozenge>
              <span> to mitigate the issue.</span>
            </>
          )}
          {thresholdConfigured && (
            <>
              <Lozenge>low traffic threshold</Lozenge>
              <span> is set to </span>
              <Lozenge>{localNumberFormat(lowTrafficThreshold)}</Lozenge>
              <span> events / 28 days </span>

              {thresholdConfiguredTooLow && (
                <>
                  <span>with warning</span>
                  <Tooltip
                    content={
                      <span>
                        {thresholdTooLowMessage}
                        <br />
                        <br />
                        Click &quot;See details&quot; to check our recommended config.
                      </span>
                    }
                    position="right"
                  >
                    <WarningIcon
                      size="small"
                      primaryColor={token('color.text.accent.orange')}
                      label="warning-icon"
                    />
                  </Tooltip>
                </>
              )}
            </>
          )}
        </p>
      </SectionMessage>
    </div>
  );
};

export default LowTrafficHelper;
